import './style.scss';

import View                         from '@widesk-ui/components/View';
import currentEditorStore           from './stores/currentEditorStore';
import React                        from 'react';
import { observer }                 from 'mobx-react-lite';
import MapModel                     from '@models/map/MapModel';
import LeftSidebar                  from '@/TokamapEditor/ux/LeftSidebar';
import leftSidebarStore             from '@/TokamapEditor/stores/leftSidebarStore';
import { useParams }                from 'react-router-dom';
import Header                       from '@/TokamapEditor/ux/Header';
import Footer                       from '@/TokamapEditor/ux/Footer';
import ProjectEditorStore           from '@/TokamapEditor/stores/ProjectEditorStore';
import currentProjectEditorStore    from '@/TokamapEditor/stores/currentProjectEditorStore';
import MapFloatingControls          from '@/TokamapEditor/ux/MapFloatingControls';
import OnboardingChecklist          from '@/TokamapEditor/ux/OnboardingChecklist';
import LogoLoader                   from '@/components/LogoLoader';
import TokaMap                      from '@/TokamapEditor/ux/TokaMap';
import { lngLatToMercator }         from '@tokamap/core';
import { isBackgroundShapeId }      from '@/TokamapEditor/helpers/planBackground';
import { mercatorToLngLat }         from '@tokamap/core';
import { Shape }                    from '@tokamap/core';
import { rotationDegToRawRotation } from '@tokamap/core';
import { rawRotationToDeg }         from '@tokamap/core';
import { shapeToLatestRawFeature }  from '@tokamap/core';
import { TokaMapInstance }          from '@tokamap/core';
import { LngLat }                   from '@tokamap/core';
import { serializeFraming }         from '@tokamap/core';
import { parseProjectThemes }       from '@tokamap/core';
import { withProjectThemes }        from '@tokamap/core';
import framedLegacyView             from '@/TokamapEditor/helpers/framedLegacyView';
import FramingOverlay               from '@/TokamapEditor/ux/Framing/FramingOverlay';
import CanvasDots                   from '@/TokamapEditor/ux/CanvasDots';
import PlanRulers                   from '@/TokamapEditor/ux/PlanRulers';
import PlanImportModal              from '@/TokamapEditor/planImport/PlanImportModal';
import PlanGeneratingOverlay        from '@/TokamapEditor/planImport/PlanGeneratingOverlay';
import DrawingShortcuts             from '@/TokamapEditor/ux/DrawingShortcuts';
import BuildingPickBar              from '@/TokamapEditor/ux/BuildingPickBar';
import OpeningToolBar               from '@/TokamapEditor/ux/OpeningToolBar';
import CutToolBar                   from '@/TokamapEditor/ux/CutToolBar';
import DecorMapLayer                from '@/TokamapEditor/decor/DecorMapLayer';
import { autorun }                  from 'mobx';
import ProjectJsonModel             from '@models/map/ProjectJsonModel';
import projectPublicationStore      from '@/TokamapEditor/stores/projectPublicationStore';
import publicProjectLink            from '@/tools/publicProjectLink';
import { documentOfMap }            from '@/TokamapEditor/stores/pairMapsWithDocuments';
import FeatureModel                 from '@/models/services/map/FeatureModel';
import ApiCollection                from '@widesk-core/models/ApiCollection';
import themeStore                   from '@/TokamapEditor/stores/themeStore';
import shapeClipboardStore          from '@/TokamapEditor/stores/shapeClipboardStore';
import { featureTypeOfShape }       from '@/TokamapEditor/helpers/featureTypes';
import { useKeyboardShortcuts }     from '@/TokamapEditor/useKeyboardShortcuts';
import useProjectThumbnail         from '@/TokamapEditor/useProjectThumbnail';
import EditorMercureListener        from '@/TokamapEditor/stores/EditorMercureListener';
import { DEFAULT_ORIGIN_CENTER }    from '@/TokamapEditor/ux/constants';
import { ZONE_SIDEBAR_WIDTH }       from '@/TokamapEditor/ux/constants';
import { SHEET_DEFAULT_HEIGHT }     from '@/TokamapEditor/ux/constants';
import useIsMobile                  from '@/hooks/useIsMobile';
import { MIN_IMAGE_SCREEN_PX }      from '@/TokamapEditor/ux/constants';
import { SHOW_SHAPE_MEASURES }      from '@/TokamapEditor/ux/constants';
import { MAX_SELECTION_SHAPES }     from '@/TokamapEditor/ux/constants';
import appStore                     from '@/stores/appStore';
import throttle                     from 'lodash/throttle';
import resolveAkamaiImageUrl        from './helpers/resolveAkamaiImageUrl';
import useDesignTokens              from '@/hooks/useDesignTokens';
import useThemeMode                 from '@widesk-ui/hooks/useThemeMode';
import shapePlaceId                 from '@/TokamapEditor/helpers/shapePlaceId';
import { resolvePlaceLinkedContent } from '@/TokamapEditor/helpers/placeLinkedContent';

const PLACE_SHAPE_KINDS: string[] = ['polygon', 'rectangle', 'circle', 'marker'];

const ProjectProvider = observer(() => {
	const { projectId } = useParams();
	const tokens = useDesignTokens();

	const projectEditor = currentProjectEditorStore.get;

	React.useEffect(() => {
		const store = new ProjectEditorStore(projectId || '0');

		currentProjectEditorStore.set(store);

		if (projectId) {
			(() => store.fetch())();
		}

		return () => {
			store.clear();
		};
	}, [projectId]);

	// Id de la dernière carte dont l'instance `@tokamap/core` a fini de charger (voir `Map.onReady`
	// ci-dessous) — comparé à `map.id` plutôt que de dépendre de `currentEditorStore.get` pour piloter
	// l'overlay de chargement : `key={map.id}` sur `<Map>` remonte tout le sous-arbre à chaque
	// changement de carte (nouvel `EditorMercureListener`, nouvelle instance MapLibre...), mais
	// `currentEditorStore.get` n'est lui jamais remis à `undefined` entre-temps (`TokaMap.tsx` n'a pas
	// de vrai destroy — voir son commentaire), donc `!currentEditorStore.get` restait vrai en
	// permanence pendant tout un changement de carte, laissant voir le conteneur vide de la nouvelle
	// carte sans aucun overlay par-dessus (contrairement au tout premier chargement, où `get` démarre
	// bien à `undefined`). Comparer à `map.id` ici, purement au rendu (pas un effet), retombe juste
	// dès le même rendu que le changement de `key` : pas de frame intermédiaire où le canvas vide de
	// la nouvelle carte serait visible sans overlay.
	const [readyMapId, setReadyMapId] = React.useState<string | number | null>(null);

	if (!projectEditor.currentMap.isLoaded) {
		return <LoadingScreen text="Chargement des cartes du projet..." />;
	}

	const map = projectEditor.currentMap;

	return (
		<View flex style={{ overflow: 'hidden' }}>
			<MapFloatingControls />

			<OnboardingChecklist />

			<PlanImportModal />

			<PlanGeneratingOverlay />

			<View bg={tokens.surface.page} h="100%">
				<Header />

				<View h="100%">
					<View
						row
						flex
						rel
						bg="#00000010"
						className="editor"
						style={{
							'--toka-canvas-bg': tokens.surface.page,
							'--editor-elevated': tokens.surface.elevated,
							'--editor-shadow': tokens.shadow.elevated,
							'--editor-text': tokens.text.default,
							'--editor-text-secondary': tokens.text.secondary,
							'--editor-border': tokens.border.default,
							'--editor-primary': tokens.color.primary,
							'--editor-error': tokens.color.error,
						} as React.CSSProperties}
					>
						<LeftSidebar />

						<Map key={map.id} map={map} onReady={() => setReadyMapId(map.id)} />
					</View>

					<Footer />
				</View>
			</View>

			{readyMapId !== map.id && <LoadingScreen text="Chargement des données de la carte..." />}
		</View>
	);
});

export default ProjectProvider;

const LoadingScreen = ({ text }: { text: string; }) => {
	const tokens = useDesignTokens();

	return (
		<View absT={0} w="100vw" h="100vh" zIndex={200} bg={tokens.surface.page} color={tokens.text.default}>
			<View flex center gap="lg">
				<LogoLoader size={64} />

				<View color={tokens.text.secondary} size={16} style={{ letterSpacing: '.02em' }}>
					{text}
				</View>
			</View>
		</View>
	);
};

const Map = observer(({ map, onReady }: { map: MapModel; onReady?: () => void; }) => {
	const isMobile = useIsMobile();
	const { mode } = useThemeMode();
	const [instance, setInstance] = React.useState<TokaMapInstance | null>(null);
	const [selectedShape, setSelectedShape] = React.useState<Shape | null>(null);
	const [selectionShapeIds, setSelectionShapeIds] = React.useState<string[]>([]);
	// Lu depuis le listener `shapeStore.onChange` ci-dessous, qui ne doit pas se réabonner à chaque
	// changement de sélection (dépend uniquement de `instance`) — évite de lire une valeur figée de
	// `selectionShapeIds` capturée à la création de l'effet.
	const selectionShapeIdsRef = React.useRef<string[]>([]);
	// `true` tant que le rectangle de sélection est en cours de tracé (Ctrl+glisser pas encore
	// relâché, voir `event.interacting` sur `selection:changed`) — lu par l'effet d'ouverture de la
	// sidebar ci-dessous pour ne PAS l'ouvrir/fermer pendant le geste : ouvrir la sidebar réduit la
	// largeur du conteneur de la carte, ce qui invaliderait l'ancre en pixels écran du rectangle en
	// cours de tracé côté `@tokamap/core` et annulerait la sélection au relâchement (bug rapporté).
	const selectionInteractingRef = React.useRef(false);

	const mercure = React.useMemo(() => new EditorMercureListener(map.urn, true), [map.urn]);

	React.useEffect(() => {
		void mercure.start();
		return () => void mercure.stop();
	}, []);

	React.useEffect(() => {
		if (!instance) return;

		// Sélection au clic gauche (BESOINS.md §4) : `@tokamap/core` émet l'évènement, l'exemple
		// décide seul quoi en faire (ici : ouvrir la sidebar) — voir CLAUDE.md.
		const unsubscribeClick = instance.mapAdapter.on((event) => {
			if (event.type === 'shape:created') {
				if (currentEditorStore.isDrawing && PLACE_SHAPE_KINDS.includes(event.shape.shapeKind ?? 'polygon')) {
					currentEditorStore.markFreshShape(event.shape.id);
					currentEditorStore.requestShapeSelection(event.shape.id);
				}
				return;
			}
			if (event.type === 'map:click') {
				setSelectedShape(null);
				currentEditorStore.setHighlightedShapes([]);
				return;
			}
			if (event.type !== 'shape:click') return;

			// Les échafaudages d'import de plan (fond posé pour le placement, formes de l'aperçu
			// global — voir `helpers/planBackground.ts` et `helpers/planShapes.ts`) ne sont PAS des
			// formes de la carte : les sélectionner ouvrirait les « réglages de forme » sur un objet
			// qui n'existe pas côté API, et cette sidebar remplacerait le panneau d'import — qui,
			// en se démontant, retire l'aperçu que l'opérateur était en train d'utiliser. C'est
			// exactement le symptôme observé : l'image disparaît, et le clic ne fait rien d'autre.
			//
			// On ne touche pas non plus à la sélection courante : le clic appartient au panneau
			// d'import, qui écoute le même évènement de son côté.
			if (isBackgroundShapeId(event.shapeId)) return;

			const shape = instance.getAllShapes()
				.find(s => s.id === event.shapeId);
			setSelectedShape(shape ?? null);
		});

		// Garde la forme affichée synchronisée avec le store (édition/undo/redo), et désélectionne
		// si elle disparaît (suppression).
		const unsubscribeStore = instance.shapeStore.onChange((shapes) => {
			setSelectedShape((current) => {
				if (!current) return current;
				return shapes
					.find(s => s.id === current.id) ?? null;
			});

			// Reflète toute édition (y compris celles faites via la sidebar en mode sélection multiple
			// elle-même) dans `currentEditorStore.sidebarShapes`, sinon ses champs resteraient figés
			// sur les valeurs d'avant l'édition.
			const ids = new Set(selectionShapeIdsRef.current);
			if (ids.size) {
				currentEditorStore.setSelectionShapes(shapes.filter(s => ids.has(s.id)));
			}

			// Réveille les écrans qui comptent les formes hors sélection — voir `shapesRevision`.
			currentEditorStore.bumpShapesRevision();
			currentEditorStore.consumePendingHighlight();

			const requested = currentEditorStore.takeRequestedShape();
			if (requested) setSelectedShape(requested);
		});

		currentEditorStore.setMap(map);
		currentEditorStore.set(instance);
		currentEditorStore.consumePendingHighlight();

		if (!currentEditorStore.isCenterDefined) {
			currentEditorStore.get.setMapView({ center: mercatorToLngLat(DEFAULT_ORIGIN_CENTER) }, {
				skipConfirm: true,
				skipHistory: true,
				skipSave: true,
			});

			currentEditorStore.get.startEditingMapView({ skipHistory: true });
		}

		return () => {
			unsubscribeClick();
			unsubscribeStore();
		};
	}, [instance]);

	React.useEffect(() => {
		// Pas de sidebar tant que le rectangle de sélection est encore en cours de tracé — voir le
		// commentaire de `selectionInteractingRef`. Le prochain changement définitif (relâchement,
		// Ctrl+clic, Échap...) redéclenchera cet effet et appliquera la bonne décision.
		if (selectionInteractingRef.current) return;

		// Sélection composée par programme (bouton "N zones liées"...) : on la laisse s'afficher sur le
		// plan sans toucher à la sidebar ouverte, qui est justement celle d'où vient le clic.
		if (currentEditorStore.consumeSkipSelectionSidebar()) return;

		// La sélection multiple prime sur le clic simple — même priorité que `useKeyboardShortcuts.ts`
		// (rare que les deux soient actives en même temps, mais garde un comportement défini).
		if (selectionShapeIds.length > 1) {
			leftSidebarStore.openFeatureSettings(true, `multi:${selectionShapeIds.join(',')}`);
		} else if (selectedShape) {
			// Clé du point d'intérêt seul pour une forme liée : passer d'une de ses formes à une autre garde
			// le même panneau (pas de fermeture/réouverture, pas de nouveau chargement de la fiche).
			const placeId = shapePlaceId(selectedShape);

			leftSidebarStore.openFeatureSettings(true, placeId ? `place:${placeId}` : `shape:${selectedShape.id}`);
			currentEditorStore.get.mapAdapter.ensureShapeVisible(
				selectedShape.id,
				isMobile ? { bottom: window.innerHeight * SHEET_DEFAULT_HEIGHT } : { left: ZONE_SIDEBAR_WIDTH },
			);
			// Ne referme que si elle affiche encore les réglages de forme : une autre sidebar
			// (points d'intérêts, images...) a pu prendre sa place sans passer par `close()` — la
			// fermer ici serait une régression (voir feedback utilisateur sur ce point précis).
		} else if (leftSidebarStore.options.reference === 'feature_settings') {
			leftSidebarStore.close();
		}
	}, [selectedShape, selectionShapeIds]);

	React.useEffect(() => {
		currentEditorStore.setSelectedShape(selectedShape);

		if (currentEditorStore.freshShapeId && currentEditorStore.freshShapeId !== selectedShape?.id) {
			currentEditorStore.markFreshShape(null);
		}

		if (currentEditorStore.promotingShapeId && currentEditorStore.promotingShapeId !== selectedShape?.id) {
			currentEditorStore.setPromotingShape(null);
		}

		// Informe les autres utilisateurs de la map de la forme sélectionnée (ou de la désélection) —
		// permet à `EditorUserStore`/`MapLibreAdapter` d'ancrer leur curseur distant sur cette forme
		// même quand leur souris a quitté la carte (ex. clic dans la sidebar de forme).
		currentEditorStore.editorUserStore.socket.emit('client:selection-change', {
			mapId: map.id,
			userId: appStore.user.id,
			shapeId: selectedShape?.id ?? null,
		});
	}, [selectedShape]);

	// Sens inverse : désélection demandée depuis l'extérieur (fermeture de la sidebar de forme,
	// `leftSidebarStore`'s `onClose` → `currentEditorStore.clearSelection()`) sans passer par un
	// clic sur la carte — sinon cet état local resterait périmé (rouvre la sidebar au clic suivant
	// sur la même forme, garde les raccourcis clavier actifs sur une forme qui semble désélectionnée).
	React.useEffect(() => {
		if (selectedShape && !currentEditorStore.selectedShape) setSelectedShape(null);
	}, [currentEditorStore.selectedShape]);

	React.useEffect(() => {
		const requested = currentEditorStore.takeRequestedShape();
		if (requested) setSelectedShape(requested);
	}, [currentEditorStore.requestedShapeId]);

	React.useEffect(() => {
		selectionShapeIdsRef.current = selectionShapeIds;

		if (!instance) return;
		const ids = new Set(selectionShapeIds);
		currentEditorStore.setSelectionShapes(ids.size ? instance.getAllShapes().filter(s => ids.has(s.id)) : []);
	}, [instance, selectionShapeIds]);

	React.useEffect(() => {
		if (!instance) return;
		return instance.mapAdapter.on((event) => {
			if (event.type === 'drawing:started') return currentEditorStore.setIsDrawing(true);
			if (event.type === 'drawing:ended') return currentEditorStore.setIsDrawing(false);
		});
	}, [instance]);

	React.useEffect(() => {
		if (!instance) return;
		return instance.mapAdapter.on((event) => {
			if (event.type !== 'selection:changed') return;
			selectionInteractingRef.current = event.interacting ?? false;
			if (event.interacting) return;
			setSelectionShapeIds(event.shapeIds);
		});
	}, [instance]);

	React.useEffect(() => {
		if (!instance) return;

		const emitCursorMove = throttle((position: LngLat) => {
			currentEditorStore.editorUserStore.socket.emit('client:cursor-move', {
				mapId: map.id,
				userId: appStore.user.id,
				position,
			});
		}, 100);

		const unsubscribe = instance.mapAdapter.on(event => {
			if (event.type === 'map:pointer-moved') {
				emitCursorMove(event.position);
				return;
			}
			if (event.type !== 'map:pointer-left') return;

			// Annule un éventuel appel throttlé encore en attente (souris déjà repartie) — sinon il se
			// déclencherait après ce `position: null` et ferait réapparaître le curseur chez les autres
			// utilisateurs avec une position déjà périmée. `position: null` (pas un évènement dédié) :
			// voir le commentaire de `client:cursor-move` dans `socketIoTypes.d.ts`.
			emitCursorMove.cancel();
			currentEditorStore.editorUserStore.socket.emit('client:cursor-move', {
				mapId: map.id,
				userId: appStore.user.id,
				position: null,
			});
		});

		return () => {
			unsubscribe();
			emitCursorMove.cancel();
		};
	}, [instance]);

	// Les thèmes vivent hors de la carte (ressource de projet, voir `themeStore`) : on les repousse dans
	// l'instance à chaque changement pour que les formes liées (`Shape.themeId`) soient redessinées.
	// `autorun` plutôt qu'un `useEffect` sur la valeur : `TokaMap` ne recrée jamais l'instance, et
	// c'est mobx qui sait quand la table change. `setThemes` déduplique par valeur, donc un passage
	// qui ne change rien ne redessine rien.
	React.useEffect(() => {
		if (!instance) return;

		// Chargement dès l'ouverture de la carte, et pas seulement quand l'utilisateur ouvre la sidebar
		// des thèmes (l'autre appelant de `load`) : sans ça, une forme liée à un thème rendrait son
		// style propre jusqu'à ce que cette sidebar soit ouverte au moins une fois.
		themeStore.load().catch(err => console.error('Chargement des thèmes du projet', err));

		return autorun(() => {
			// Avant le chargement, la table est vide : elle effacerait les thèmes venus avec le document.
			if (!themeStore.isLoaded) return;

			instance.setThemes(themeStore.coreThemes);
			// Thème posé d'office sur les formes nouvellement dessinées — voir `themeStore.defaultTheme`.
			instance.setDefaultThemeId(themeStore.defaultTheme?.id);
		});
	}, [instance]);

	useKeyboardShortcuts(instance, selectedShape?.id ?? null, selectionShapeIds);

	useProjectThumbnail(instance);

	return (
		<>
			<TokaMap
				config={{
					// Partagé par les cartes du projet : copier sur l'une, coller sur une autre.
					clipboard: shapeClipboardStore.forMap(currentProjectEditorStore.get.project.id, String(map.urn)),
					// Le document vient du PROJET et non de la carte seule : c'est la réponse projet qui porte
					// les thèmes. Elle rend toutes les cartes d'un coup, on n'en garde qu'une.
					loadJson: async () => {
						try {
							const projectJson = new ProjectJsonModel({ id: currentProjectEditorStore.get.project.id });

							await projectJson.fetch();

							const documents = (projectJson.json as { maps?: unknown[] })?.maps ?? [];
							const json: any = documentOfMap(documents, map.id);

							// Ouvrir une carte vide ferait enregistrer un plan sans aucune forme au premier geste.
							if (!json) throw new Error(`Le projet ne rend aucun document pour cette carte.`);

							currentEditorStore.setIsCenterDefined(!!json.configurations.center);
							currentEditorStore.readMapKindFrom(json.configurations);
							currentEditorStore.documentOrigin = json.configurations.center
								? {
									center: mercatorToLngLat(json.configurations.center),
									zoom: json.configurations.zoom ?? 16,
									zoomMax: json.configurations.zoomMax,
									rotationDeg: rawRotationToDeg(json.configurations.rotation ?? 0),
								}
								: null;

							// Les thèmes dès le premier rendu : posés après coup par `themeStore`, ils faisaient tout
							// redessiner juste après l'ouverture. Son passage suivant, identique, ne redessine rien.
							return withProjectThemes(json, parseProjectThemes((projectJson.json as { themes?: unknown })?.themes));
						} finally {
							mercure.resumeMessageQueue();
						}
					},
					resolveImageUrl: resolveAkamaiImageUrl,
					resolveLinkedContent: resolvePlaceLinkedContent,
					benchmark: {
						// La carte telle qu'un client l'ouvrira : sa dernière publication, banc d'essai lancé d'office.
						visitorTestUrl: () => projectPublicationStore.isPublishedOnce
							? `${publicProjectLink(currentProjectEditorStore.get.project)}?benchmark`
							: null,
					},
					minImageOverlayScreenPx: MIN_IMAGE_SCREEN_PX,
					includeImages: true,
					includeIndirectImages: true,
					showDrawingMeasures: SHOW_SHAPE_MEASURES,
					maxSelectionShapes: MAX_SELECTION_SHAPES,
					editMode: true,
					theme: mode,

					onSave: async event => {
						currentEditorStore.setIsCenterDefined(true);

						console.log(`[onSave] "${event.shapes.map(s => s.id)}" (${event.kind}) — sauvegarde en cours...`);

						// Le fond d'un import de plan (voir `helpers/planBackground.ts`) n'est qu'un
						// échafaudage de placement, jamais une forme de la carte : il ne doit devenir
						// une Feature sous aucun chemin — y compris ceux que l'opérateur déclenche
						// lui-même sur la forme (double-clic, "Modifier", Suppr), qui rouvrent son
						// édition sans le `skipSave` posé à sa création.
						const shapes = event.shapes.filter(shape => !isBackgroundShapeId(shape.id));

						if (!shapes.length) return;

						if (event.kind === 'delete') {
							const featureIds = shapes.map(shape => {
								const id = shapeToLatestRawFeature(shape).geometry.properties?.id || '';
								return String(currentEditorStore.getFeatureIdFromShapeId(id));
							});

							// Une seule requête `DELETE .../features?id[]=...` pour tout le groupe, au lieu d'un
							// `DELETE` par forme.
							await new ApiCollection(FeatureModel).setFilter('id', featureIds).destroy();

							return;
						}

						const shapesData = shapes.map(shape => {
							const rawFeature = shapeToLatestRawFeature(shape);
							const shapeId = rawFeature.geometry.properties?.id || '';
							const entityId = (rawFeature.geometry.properties?.entity as any)?.entity?.id;

							return {
								shapeId,
								attributes: {
									coordinates: rawFeature.geometry.coordinates,
									extra: rawFeature.geometry.properties?.extra,
									featureType: featureTypeOfShape(shape).urn,
									layer: shape.layer?.toString(),
									map: map.urn,
									// `null` explicite, pas `undefined` : l'API patch en `application/merge-patch+json`
									// (RFC 7396) — une clé absente du body ("undefined", supprimé par `JSON.stringify`)
									// signifie "ne pas toucher au champ", alors qu'on veut ici explicitement le vider
									// quand la forme n'est plus liée à aucun point d'intérêt (ex. changement d'action).
									entityUrn: entityId ? `$:map:place:${entityId}` : null,
									// `null` explicite pour la même raison qu'`entityUrn` ci-dessus.
									theme: shape.themeId ? `$:map:theme:${shape.themeId}` : null,
								},
							};
						});

						const attributesByFeature = new WeakMap<FeatureModel, Record<string, unknown>>();

						const entries = shapesData.map(({ shapeId, attributes }) => {
							const apiFeature = new FeatureModel(
								event.kind === 'create' ? attributes : { id: currentEditorStore.getFeatureIdFromShapeId(shapeId) },
							);

							attributesByFeature.set(apiFeature, attributes);

							return { shapeId, apiFeature };
						});

						const coll = new ApiCollection(FeatureModel, entries.map(({ apiFeature }) => apiFeature));

						await coll.batch(
							{ params: { map: map.id }, method: event.kind === 'create' ? 'post' : 'patch' },
							apiFeature => attributesByFeature.get(apiFeature)!,
						);

						entries.forEach(({ shapeId, apiFeature }) => {
							currentEditorStore.shapeIdToFeatureId[shapeId] = apiFeature.id.toString();
						});
					},

					onSaveConfiguration: async ({
						view,
						defaultShapeStyle,
						tileLayerName,
						osmOptions,
						renderingOptions,
						backgroundColor,
						formatVersion,
						geolocation,
						allowPitch,
						framing,
					}) => {
						const startView = framedLegacyView(view);

						await map.patch({
							configuration: {
								...startView,
								center: lngLatToMercator(startView.center),
								rotation: rotationDegToRawRotation(startView.rotationDeg),
								framing: framing ? serializeFraming(framing) : null,
								defaultShapeStyle,
								tileLayerName,
								osmOptions,
								renderingOptions,
								backgroundColor,
								formatVersion,
								geolocation,
								allowPitch,
								...currentEditorStore.mapKindConfiguration,
							},
						});

						currentEditorStore.setIsCenterDefined(true);
					},

					onMigrateDocument: async () => {
						// await map.patch({
						// 	configuration: {
						// 		...map.configuration,
						// 		formatVersion: 2, // TODO
						// 	},
						// });
						alert(`La fonctionnalité de migration d'une carte, n'est pas encore disponible`); // TODO
						throw 'TODO';
					},
				}}
				onReady={instance => {
					setInstance(instance);
					onReady?.();
				}}
			/>

			<CanvasDots />

			<PlanRulers />

			<FramingOverlay />

			<DrawingShortcuts />

			<BuildingPickBar />

			<OpeningToolBar />

			<CutToolBar />

			<DecorMapLayer />
		</>
	);
});
